import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Typography, Container } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getCanonicalUrl } from '../utils/canonicalUrl';

export default function FAQ() {
  const location = useLocation();
  const canonicalUrl = getCanonicalUrl(location.pathname);
  return (
    <Box sx={{ color: 'common.white' }}>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <title>AI Consulting FAQ</title>
        <meta name="description" content="Frequently asked questions about our AI consulting services." />
        <meta property="og:title" content="AI Consulting FAQ" />
        <meta property="og:description" content="Answers to common questions about our approach and offerings." />
        <meta property="og:image" content="/assets/visuals/re-profile-blue-ai-hud.webp" />
      </Helmet>
      <Box
        sx={{
          position: 'relative',
          backgroundImage:
            'linear-gradient(rgba(24,45,70,0.8), rgba(53,85,111,0.8)), url("/assets/visuals/re-profile-blue-ai-hud.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          textAlign: 'center',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container>
          <Typography variant="h2" component="h1" gutterBottom>
            FAQ
          </Typography>
        </Container>
      </Box>
      <Typography variant="h6" component="h2">What services do you offer?</Typography>
      <Typography paragraph>
        We provide AI Readiness Assessments, AI Policy Development, End-to-End AI Consulting and a Free AI Assessment gateway service.
      </Typography>
      <Typography variant="h6" component="h2">How do I get started?</Typography>
      <Typography paragraph>
        The easiest way is to request our Free AI Assessment or schedule a consultation through the contact page.
      </Typography>
      <Typography variant="h6" component="h2">Do you work with companies of all sizes?</Typography>
      <Typography paragraph>
        Yes. From startups to large enterprises, we tailor our approach to meet each client's unique needs.
      </Typography>
      <Typography variant="h6" component="h2">What is your approach to projects?</Typography>
      <Typography paragraph>
        We are pragmatists who deliver fast, iterative results. Our client-first culture emphasises clear communication and solving real business problems.
      </Typography>
      <Typography variant="h6" component="h2">Which industries do you serve?</Typography>
      <Typography paragraph>
        Our experience spans finance, retail, manufacturing, health care and many more. If your organisation faces complex problems, we can likely help.
      </Typography>

      <Typography variant="h6" component="h2">How long does an AI Readiness Assessment take?</Typography>
      <Typography paragraph>
        Most assessments are completed within three to four weeks, depending on your organisation's size and availability of stakeholders.
      </Typography>

      <Typography variant="h6" component="h2">Do you provide training for internal teams?</Typography>
      <Typography paragraph>
        Yes. Knowledge transfer through workshops and paired delivery is baked into every engagement so your team can maintain momentum.
      </Typography>

      <Typography variant="h6" component="h2">What size budgets do you work with?</Typography>
      <Typography paragraph>
        We scope each project individually and can start with focused prototypes or scale to enterprise programmes, depending on your needs.
      </Typography>

      <Typography variant="h6" component="h2">Can you integrate with our existing systems?</Typography>
      <Typography paragraph>
        Absolutely. Our solutions are designed to complement your current technology stack rather than replace it.
      </Typography>

      <Typography variant="h6" component="h2">How do you ensure AI projects remain ethical?</Typography>
      <Typography paragraph>
        Through our AI Policy Development service we define governance structures, bias mitigation strategies and compliance checks from day one.
      </Typography>

      <Typography variant="h6" component="h2">Do you offer ongoing support after deployment?</Typography>
      <Typography paragraph>
        We provide optional maintenance, monitoring and improvement cycles to keep your models delivering value long term.
      </Typography>

      <Typography variant="h6" component="h2">What if we're unsure where AI fits in our business?</Typography>
      <Typography paragraph>
        Our Free AI Assessment uncovers high-impact opportunities and gives you a clear starting point for applying AI.
      </Typography>

      <Typography variant="h6" component="h2">Can you help with data strategy and architecture?</Typography>
      <Typography paragraph>
        Yes. We assist with pipelines, storage solutions and best practices so your data is ready for advanced analytics.
      </Typography>

      <Typography variant="h6" component="h2">Do you build custom models or use off-the-shelf tools?</Typography>
      <Typography paragraph>
        We choose the approach that best meets your goals, whether crafting bespoke models or leveraging proven open source libraries.
      </Typography>

      <Typography variant="h6" component="h2">Is on-site consulting available?</Typography>
      <Typography paragraph>
        We are a remote-first consultancy but can arrange in-person sessions and workshops where they add value.
      </Typography>

      <Typography variant="h6" component="h2">How do you measure success?</Typography>
      <Typography paragraph>
        Clear metrics are agreed upon upfront, ensuring our solutions drive tangible business outcomes you can track.
      </Typography>
    </Box>
  );
}
